import React, { useState, useContext } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CardHeader from '@material-ui/core/CardHeader';
import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import { createDirectory } from 'store/directories';
import { createSource, initializeSource } from 'store/sources';
import useAnalytics from 'helpers/useAnalytics';
import SourceContext from 'helpers/SourceContext';
import makeSourceCreateBaseUrl from 'pages/Directory/helpers/makeSourceCreateBaseUrl';
import HelpArticleLink from 'components/Page/HelpArticleLink';
import { HelpArticleLinkContext } from 'components/Router/Router';
import Base from 'pages/Directory/Base';
import NewSourceCard from 'pages/SourceCreate/components/SourceCard/NewSourceCard';
import AdditionalFileSyncIcon from 'components/Icons/AdditionalFileSyncIcon';

// Consts
import sourceData from 'pages/Directory/sourceData';
import SourceType from 'types/Source';
import * as Types from 'pages/Directory/typings';
import { HOME_URL, DIRECTORIES_URL, SOURCE_URL } from 'components/Router/consts';
import { NAME_YOUR_DATA_URL, DEMO_DATA_SOURCE_TYPE_KEY } from 'pages/Directory/consts';

interface Props {
  sourcesWithDemoData: SourceType[];
}

function ChooseSourceType({ sourcesWithDemoData }: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const analytics = useAnalytics();
  const history = useHistory();
  const sourceContext = useContext(SourceContext);
  const helpArticleLink = React.useContext(HelpArticleLinkContext) as string | null;
  const { sourceInfo, setSourceInfo } = sourceContext;
  const [sourceType, setSourceType] = useState(null);
  const directoryIdUrlMatch = window.location.href.match(/directories\/.*\/create-source/);
  const directoryId = directoryIdUrlMatch ? directoryIdUrlMatch[0].split('/')[1] : undefined;
  const baseUrl = makeSourceCreateBaseUrl(directoryId);
  const isDemoDataAvailable = _isEmpty(sourcesWithDemoData);

  const data = {
    title: 'Choose a Data Sync Method',
    subheader: sourceInfo.isPrimary
      ? 'Choose an integration method for your Primary Source within this Directory. The Primary Source controls addition and deletion of people in Sift.'
      : 'Choose an integration method for your Complementary Source within this Directory. The Complementary Source only appends data, and will never create or delete people.',
  };

  const handleSourceSelect = (value: any) => {
    setSourceType(value);
  };

  const handleNext = async () => {
    analytics.track('Wizard - Source Type Step Completed', {
      category: 'AD - Directories',
      label: sourceType,
    });

    if (sourceType === DEMO_DATA_SOURCE_TYPE_KEY) {
      // (notes - pearl) When source type is demo data, we initialize the source here
      // then navigate directly to its source dashboard, skipping the rest of the wizard
      const label = 'Sift Demo Data';
      const directoryCreateRes = (await dispatch(createDirectory(label))) as any;
      const directoryId = directoryCreateRes.body.id;

      const sourceCreateRes = (await dispatch(
        createSource(directoryId, DEMO_DATA_SOURCE_TYPE_KEY, label, sourceInfo.isPrimary),
      )) as any;
      const sourceId = sourceCreateRes.body.id;

      dispatch(initializeSource(sourceId));

      history.push(`${HOME_URL}${DIRECTORIES_URL}/${directoryId}${SOURCE_URL}/${sourceId}`);
    } else {
      setSourceInfo({ ...sourceInfo, type: sourceType });

      history.push(`${baseUrl}${NAME_YOUR_DATA_URL}`);
    }
  };

  return (
    <Base onNext={handleNext} disableNextButton={!sourceType}>
      <CardHeader className={classes.header} title={data.title} subheader={data.subheader} />
      {helpArticleLink && (
        <div className={classes.link}>
          <HelpArticleLink
            link="http://help.justsift.com/en/articles/8312252-sources"
            text="Learn more about Sources ❯"
          />
        </div>
      )}
      <div className={classes.cardContainer}>
        {Object.values(sourceData).map((info: Types.SourceInfo) => {
          if (info.type === DEMO_DATA_SOURCE_TYPE_KEY) {
            return (
              <NewSourceCard
                key={info.type}
                sourceInfo={info}
                isSelected={sourceType === info.type}
                handleSourceSelect={() => handleSourceSelect(info.type)}
                isSourceAvailable={isDemoDataAvailable && sourceInfo.isPrimary}
              />
            );
          }

          return (
            <NewSourceCard
              key={info.type}
              sourceInfo={info}
              isSelected={sourceType === info.type}
              handleSourceSelect={() => handleSourceSelect(info.type)}
            />
          );
        })}
      </div>
      <Paper className={classes.altUploadContainer}>
        <div className={classes.iconContainer}>
          <AdditionalFileSyncIcon />
        </div>
        <span className={classes.altUploadCopy}>
          We additionally support direct integrations with Okta, UKG, and LDAP that require a bit of
          additional setup. To use one of these source types,{' '}
          <Link href="mailto:support@justsift.com" className="intercom-chat" color="secondary">
            reach out to our Support Team
          </Link>
          .
        </span>
      </Paper>
    </Base>
  );
}

const useStyles = makeStyles(theme => ({
  header: {
    marginBottom: theme.spacing(),
    paddingBottom: 0,
  },
  link: {
    marginLeft: theme.spacing(3),
  },
  cardContainer: {
    display: 'inline-flex',
    justifyContent: 'center',
    padding: 0,
    marginBottom: theme.spacing(4),
    marginLeft: theme.spacing(3),
  },
  altUploadContainer: {
    maxWidth: 1044,
    height: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  iconContainer: {
    maxWidth: 36,
    maxHeight: 36,
  },
  altUploadCopy: {
    marginLeft: theme.spacing(),
    fontSize: theme.typography.body1.fontSize,
  },
}));

export default ChooseSourceType;
